import * as THREE from 'three';
import { mutedMicTexture } from './helpers/texturesAndGeometries';

function AudioAnalyser3D({ mainContainer, width }) {
  // Create the main container with a high renderOrder to render in front of VRChair.mesh
  const container = new THREE.Object3D();
  container.renderOrder = 10; // Higher than VRChair.mesh (renderOrder = 1)

  // Create the main green circle
  const circleGeometry = new THREE.CircleGeometry(width, 32);
  const circleMaterial = new THREE.MeshBasicMaterial({
    color: 0x7caf00,
    side: THREE.FrontSide,
    depthWrite: false,
    depthTest: false, // Disable depth testing to prevent mixing
    transparent: true,
  });
  const markSeatMic = (mesh) => {
    mesh.type = 'seatMic';
    mesh.name = 'seatMic';
  };

  const outerCircle = new THREE.Mesh(circleGeometry, circleMaterial);
  container.add(outerCircle);
  outerCircle.renderOrder = 11;
  markSeatMic(outerCircle);

  // Create a slightly larger circle for the glow effect
  const glowGeometry = new THREE.CircleGeometry(width * .9, 32);
  const glowMaterial = new THREE.MeshBasicMaterial({
    color: 0x577a00,
    side: THREE.FrontSide,
    depthWrite: false,
    depthTest: false,
    transparent: true,
  });
  const innerCircle = new THREE.Mesh(glowGeometry, glowMaterial);
  container.add(innerCircle);
  innerCircle.position.z += .1;
  innerCircle.renderOrder = 12;
  markSeatMic(innerCircle);

  // Create the three white rounded rectangle planes
  const planeWidth = width * 0.2;
  const planeHeight = width * 0.2;
  const cornerRadius = planeWidth * 0.4; // 40% of width for rounded corners

  const roundedRectShape = new THREE.Shape();
  roundedRectShape.moveTo(-planeWidth / 2, -planeHeight / 2 + cornerRadius);
  roundedRectShape.lineTo(-planeWidth / 2, planeHeight / 2 - cornerRadius);
  roundedRectShape.quadraticCurveTo(-planeWidth / 2, planeHeight / 2, -planeWidth / 2 + cornerRadius, planeHeight / 2);
  roundedRectShape.lineTo(planeWidth / 2 - cornerRadius, planeHeight / 2);
  roundedRectShape.quadraticCurveTo(planeWidth / 2, planeHeight / 2, planeWidth / 2, planeHeight / 2 - cornerRadius);
  roundedRectShape.lineTo(planeWidth / 2, -planeHeight / 2 + cornerRadius);
  roundedRectShape.quadraticCurveTo(planeWidth / 2, -planeHeight / 2, planeWidth / 2 - cornerRadius, -planeHeight / 2);
  roundedRectShape.lineTo(-planeWidth / 2 + cornerRadius, -planeHeight / 2);
  roundedRectShape.quadraticCurveTo(-planeWidth / 2, -planeHeight / 2, -planeWidth / 2, -planeHeight / 2 + cornerRadius);

  const plane = new THREE.ShapeGeometry(roundedRectShape);
  const planeMaterial = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    side: THREE.FrontSide,
    depthWrite: false,
    depthTest: false, // Disable depth testing
    transparent: true,
  });

  const mesh1 = new THREE.Mesh(plane, planeMaterial);
  container.add(mesh1);
  mesh1.position.x = -(width * 0.2 * 2);
  mesh1.renderOrder = 13;
  markSeatMic(mesh1);

  const mesh2 = new THREE.Mesh(plane, planeMaterial);
  container.add(mesh2);
  mesh2.renderOrder = 13;
  mesh2.position.x = width * 0.2 * 2;
  markSeatMic(mesh2);

  const mesh3 = new THREE.Mesh(plane, planeMaterial);
  container.add(mesh3);
  mesh3.renderOrder = 13;
  markSeatMic(mesh3);

  const mutedCircleMaterial = new THREE.MeshBasicMaterial({
    color: 0x8a8a8a,
    side: THREE.FrontSide,
    depthWrite: false,
    depthTest: false,
    transparent: true,
  });
  const mutedCircle = new THREE.Mesh(circleGeometry, mutedCircleMaterial);
  mutedCircle.renderOrder = 11;
  mutedCircle.visible = false;
  container.add(mutedCircle);
  markSeatMic(mutedCircle);

  const iconSize = width * 1.2;
  const mutedIconGeometry = new THREE.PlaneGeometry(iconSize, iconSize);
  const mutedIconMaterial = new THREE.MeshBasicMaterial({
    map: mutedMicTexture,
    side: THREE.FrontSide,
    depthWrite: false,
    depthTest: false,
    transparent: true,
  });
  const mutedIcon = new THREE.Mesh(mutedIconGeometry, mutedIconMaterial);
  mutedIcon.position.z += 0.1;
  mutedIcon.renderOrder = 14;
  mutedIcon.visible = false;
  container.add(mutedIcon);
  markSeatMic(mutedIcon);

  let isMuted = false;

  // Add container to the mainContainer (should be VRChair Object3D, not VRChair.mesh)
  mainContainer.add(container);

  // Store meshes for external access
  const meshes = { mesh1, mesh2, mesh3, innerCircle, outerCircle, mutedCircle, mutedIcon };

  // Set position with z-offset
  function setPosition(position) {
    container.position.x = position.x;
    container.position.y = position.y;
    container.position.z = position.z; // Ensure forward positioning
  }

  function setMuted(muted) {
    isMuted = !!muted;
    outerCircle.visible = !isMuted;
    innerCircle.visible = !isMuted;
    mesh1.visible = !isMuted;
    mesh2.visible = !isMuted;
    mesh3.visible = !isMuted;
    mutedCircle.visible = isMuted;
    mutedIcon.visible = isMuted;
    if (isMuted) {
      mesh1.scale.y = 1;
      mesh2.scale.y = 1;
      mesh3.scale.y = 1;
    }
  }

  // Update function with GSAP for smooth scaling animations
  function update({ scaleMesh1, scaleMesh2, scaleMesh3 }) {
    if (isMuted) return;
    mesh1.scale.y = scaleMesh1;
    mesh2.scale.y = scaleMesh2;
    mesh3.scale.y = scaleMesh3;
  }

  return { update, meshes, setPosition, setMuted, container, outerCircle, innerCircle, get isMuted() { return isMuted; } };
}

export default AudioAnalyser3D;